// Client-side validation. The server re-validates everything; these checks give
// respondents immediate feedback with the same messages.
import { isAnswered } from './logic';
import { flowAnswers, sectionRuns } from './randomize';
import { MAX_RICH_LENGTH } from './rich';
import { baseId, resolve } from './resolve';
import type { AnswerRecord, AnswerValue, Answers, Condition, Definition, Issue, Question } from './types';
import { coreT, type CoreMessageKey, type Locale } from './i18n';
import {
  CAPTCHA_PLACEMENTS,
  DEFAULT_TOTAL,
  effectiveScale,
  hasOptions,
  isChoice,
  isDisplayOnly,
  isFieldName,
  isHexColor,
  isLanguageCode,
  isUploadUrl,
  isValidMediaUrl,
  MAX_SIGNATURE_LENGTH,
  MAX_TOOLTIP_LENGTH,
  MAX_UPLOAD_BYTES,
  OPERATORS,
  jumpsOf,
  otherKey,
  PAGE_MODES,
  subItems,
  THEME_FONTS,
  THEME_ZOOMS,
  TEXT_ALIGNS,
} from './types';

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const QUESTION_CODE = /^[A-Za-z0-9_-]{1,32}$/;
const MAX_TEXT = 10000;
export const MAX_OTHER_TEXT = 1000;
const SIGNATURE = /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/;
/** Files picked on a phone before they are uploaded during sync. */
const LOCAL_FILE = /^(file|content):\/\//;

const isRecord = (v: unknown): v is AnswerRecord => !!v && typeof v === 'object' && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Validates one answer. `other` is the "Other, please specify" text, when the question has that option. */
export function validateAnswer(q: Question, value: AnswerValue | undefined, locale: Locale = 'en', other?: AnswerValue): string | null {
  const t = (key: CoreMessageKey, ...args: (string | number)[]) => coreT(locale, key, ...args);
  if (isDisplayOnly(q.type)) return null;
  if (!isAnswered(value)) return q.required ? t('ans.required') : null;
  const v = q.validation ?? {};
  const otherError = (ids: unknown[]): string | null => {
    const chosen = q.options?.find((o) => o.other && ids.includes(o.id));
    if (!chosen) return null;
    if (typeof other !== 'string' || !other.trim()) return t('ans.specify_other');
    return [...other.trim()].length > MAX_OTHER_TEXT ? t('ans.too_long') : null;
  };
  switch (q.type) {
    case 'short_text':
    case 'long_text': {
      if (typeof value !== 'string') return t('ans.expected_text');
      const n = [...value.trim()].length;
      if (n > MAX_TEXT) return t('ans.too_long');
      if (v.minLength !== undefined && n < v.minLength) return t('ans.min_length', v.minLength);
      if (v.maxLength !== undefined && n > v.maxLength) return t('ans.max_length', v.maxLength);
      return null;
    }
    case 'single_choice':
    case 'dropdown':
      if (typeof value !== 'string' || !q.options?.some((o) => o.id === value)) return t('ans.choose_one');
      return otherError([value]);
    case 'multiple_choice': {
      if (!Array.isArray(value)) return t('ans.expected_list');
      if (!value.every((id) => q.options?.some((o) => o.id === id))) return t('ans.choose_listed');
      const n = new Set(value).size;
      if (v.minSelections !== undefined && n < v.minSelections) return t('ans.min_selections', v.minSelections);
      if (v.maxSelections !== undefined && n > v.maxSelections) return t('ans.max_selections', v.maxSelections);
      return otherError(value);
    }
    case 'ranking': {
      if (!Array.isArray(value)) return t('ans.expected_list');
      const ranked = value.filter((id): id is string => typeof id === 'string');
      const optionIds = q.options?.map((o) => o.id) ?? [];
      return ranked.length === optionIds.length && new Set(ranked).size === optionIds.length && ranked.every((id) => optionIds.includes(id))
        ? null
        : t('ans.choose_listed');
    }
    case 'number': {
      if (!finite(value)) return t('ans.expected_number');
      if (v.integer && !Number.isInteger(value)) return t('ans.whole_number');
      if (v.min !== undefined && value < v.min) return t('ans.min', v.min);
      if (v.max !== undefined && value > v.max) return t('ans.max', v.max);
      return null;
    }
    case 'rating':
    case 'nps': {
      const s = effectiveScale(q);
      return typeof value === 'number' && Number.isInteger(value) && value >= s.min && value <= s.max ? null : t('ans.rating_range', s.min, s.max);
    }
    case 'slider': {
      const s = effectiveScale(q);
      if (!finite(value) || value < s.min || value > s.max) return t('ans.range', s.min, s.max);
      const step = s.step ?? 1;
      const k = (value - s.min) / step;
      return Math.abs(k - Math.round(k)) < 1e-9 ? null : t('ans.step', step);
    }
    case 'date': {
      if (typeof value !== 'string' || !DATE.test(value)) return t('ans.expected_date');
      const d = new Date(value + 'T00:00:00Z');
      return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(value) ? null : t('ans.valid_date');
    }
    case 'yes_no':
      return typeof value === 'boolean' ? null : t('ans.yes_no');
    case 'matrix': {
      if (!isRecord(value)) return t('ans.choose_listed');
      const rows = q.rows ?? [];
      const col = (id: unknown) => q.options?.some((o) => o.id === id);
      for (const [rowId, cell] of Object.entries(value)) {
        if (!rows.some((r) => r.id === rowId)) return t('ans.choose_listed');
        if (cell === null) continue;
        if (q.matrixMultiple ? !Array.isArray(cell) || !cell.every(col) : !col(cell)) return t('ans.choose_listed');
      }
      if (q.required && rows.some((r) => !isAnswered(value[r.id]))) return t('ans.every_row');
      return null;
    }
    case 'constant_sum': {
      if (!isRecord(value)) return t('ans.expected_number');
      const items = q.options ?? [];
      let sum = 0;
      for (const [id, n] of Object.entries(value)) {
        if (!items.some((o) => o.id === id)) return t('ans.choose_listed');
        if (n === null) continue;
        if (!finite(n) || n < 0) return t('ans.non_negative');
        if (v.integer && !Number.isInteger(n)) return t('ans.whole_number');
        sum += n;
      }
      const total = q.total ?? DEFAULT_TOTAL;
      return Math.abs(sum - total) < 1e-9 ? null : t('ans.sum_total', total, Math.round(sum * 1000) / 1000);
    }
    case 'text_form': {
      if (!isRecord(value)) return t('ans.expected_text');
      const fields = q.options ?? [];
      for (const [id, text] of Object.entries(value)) {
        if (!fields.some((o) => o.id === id)) return t('ans.choose_listed');
        if (text === null) continue;
        if (typeof text !== 'string') return t('ans.expected_text');
        const n = [...text.trim()].length;
        if (n > MAX_TEXT) return t('ans.too_long');
        if (v.maxLength !== undefined && n > v.maxLength) return t('ans.max_length', v.maxLength);
      }
      if (q.required && fields.some((f) => !isAnswered(value[f.id]))) return t('ans.every_field');
      return null;
    }
    case 'file_upload': {
      if (!isRecord(value)) return t('ans.bad_file');
      const { url, name, size } = value;
      const okUrl = isUploadUrl(url) || (typeof url === 'string' && LOCAL_FILE.test(url));
      if (!okUrl || typeof name !== 'string' || !name || name.length > 255) return t('ans.bad_file');
      if (!finite(size) || size < 0 || size > MAX_UPLOAD_BYTES) return t('ans.file_too_big', MAX_UPLOAD_BYTES >> 20);
      return null;
    }
    case 'signature':
      return typeof value === 'string' && value.length <= MAX_SIGNATURE_LENGTH && SIGNATURE.test(value) ? null : t('ans.bad_signature');
    case 'location': {
      if (!isRecord(value)) return t('ans.bad_location');
      const { lat, lng, accuracy } = value;
      if (!finite(lat) || !finite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return t('ans.bad_location');
      if (accuracy !== undefined && accuracy !== null && (!finite(accuracy) || accuracy < 0)) return t('ans.bad_location');
      return null;
    }
  }
  return t('ans.unsupported');
}

/** Validates one resolved question against all answers (handles the "Other" text). */
export function validateQuestion(q: Question, answers: Answers, locale: Locale = 'en'): string | null {
  return validateAnswer(q, answers[q.id], locale, answers[otherKey(q.id)]);
}

/** Validates all visible questions; returns field errors keyed by question id. */
export function validateAnswers(def: Definition, answers: Answers, locale: Locale = 'en'): Record<string, string> {
  const r = resolve(def, answers);
  const errors: Record<string, string> = {};
  for (const q of r.questions) {
    if (!r.visible[q.id]) continue;
    const err = validateQuestion(q, answers, locale);
    if (err) errors[q.id] = err;
  }
  return errors;
}

/** Answers restricted to visible, non-empty questions — what gets submitted. */
export function cleanAnswers(def: Definition, answers: Answers): Answers {
  const r = resolve(def, answers);
  // The respondent's randomizer picks travel with the answers.
  const out: Answers = flowAnswers(def, answers);
  for (const q of r.questions) {
    const v = answers[q.id];
    if (!r.visible[q.id] || isDisplayOnly(q.type) || !isAnswered(v)) continue;
    out[q.id] = trimAnswer(v!);
    const ids = Array.isArray(v) ? v : [v];
    const other = answers[otherKey(q.id)];
    if (q.options?.some((o) => o.other && ids.includes(o.id)) && typeof other === 'string' && other.trim()) out[otherKey(q.id)] = other.trim();
  }
  return out;
}

function trimAnswer(v: AnswerValue): AnswerValue {
  if (typeof v === 'string') return v.trim();
  if (isRecord(v)) {
    const out: AnswerRecord = {};
    for (const [k, x] of Object.entries(v)) {
      if (!isAnswered(x)) continue;
      out[k] = typeof x === 'string' ? x.trim() : x;
    }
    return out;
  }
  return v;
}

/** Structural checks; mirrors Definition.Check in Go. Publishing requires none. */
export function checkDefinition(def: Definition, locale: Locale = 'en'): Issue[] {
  const issues: Issue[] = [];
  const add = (questionId: string | undefined, key: CoreMessageKey, ...args: (string | number)[]) =>
    issues.push({ questionId, message: coreT(locale, key, ...args) });
  if (def.questions.length === 0) add(undefined, 'def.no_questions');
  const pageMode = def.settings.pageMode;
  if (pageMode !== undefined && !PAGE_MODES.includes(pageMode)) add(undefined, 'def.bad_page_mode');
  const captcha = def.settings.captcha;
  if (captcha !== undefined && !CAPTCHA_PLACEMENTS.includes(captcha)) add(undefined, 'def.bad_captcha');
  const endingIds = new Set<string>();
  for (const e of def.endings ?? []) {
    if (!ID.test(e.id) || endingIds.has(e.id)) add(undefined, 'def.bad_ending_id');
    endingIds.add(e.id);
    if (!e.name.trim()) add(undefined, 'def.ending_name');
    if (e.redirectUrl && !isHttpsUrl(e.redirectUrl)) add(undefined, 'def.ending_redirect', e.name);
  }
  const fields = new Set<string>();
  for (const f of def.settings.hiddenFields ?? []) {
    if (!isFieldName(f) || fields.has(f.toLowerCase()) || f === 'loop' || f === 'score') add(undefined, 'def.bad_hidden_field', f);
    fields.add(f.toLowerCase());
  }
  checkLanguage(def, add);

  const index = new Map<string, number>();
  def.questions.forEach((q, i) => index.set(q.id, i));
  const loopOf = loopSections(def);
  const loopTitle = (id: string) => {
    const q = def.questions[index.get(id) ?? -1];
    const title = q?.sectionTitle?.trim();
    return title && loopOf.has(title) ? title : undefined;
  };
  for (const [title, src] of loopOf) {
    const srcIdx = index.get(src);
    const first = def.questions.findIndex((q) => q.sectionTitle?.trim() === title);
    const srcQ = srcIdx === undefined ? undefined : def.questions[srcIdx];
    if (!srcQ || srcIdx! >= first || srcQ.type !== 'multiple_choice' || srcQ.sectionTitle?.trim() === title) add(undefined, 'def.bad_loop', title);
  }

  checkRandomizers(def, add);
  const sectionOf = new Map(def.questions.map((q) => [q.id, q.sectionTitle?.trim() ?? '']));
  const randomized = new Set((def.settings.randomizers ?? []).flatMap((r) => (Array.isArray(r?.sections) ? r.sections.map((t) => String(t).trim()) : [])));
  // Logic that reaches into a randomizer section from outside it can find its target hidden.
  const reachesIn = (from: Question, id: string) => {
    const title = sectionOf.get(id);
    return !!title && randomized.has(title) && title !== (from.sectionTitle?.trim() ?? '');
  };

  const seen = new Map<string, number>();
  const seenCodes = new Set<string>();
  def.questions.forEach((q, i) => {
    const n = i + 1;
    if (!ID.test(q.id)) {
      add(q.id, 'def.invalid_id', n);
      return;
    }
    if (seen.has(q.id)) add(q.id, 'def.duplicate_id', n, q.id);
    seen.set(q.id, i);
    if (!q.title.trim()) add(q.id, 'def.needs_title', n);
    const code = q.questionCode?.trim();
    if (code && !QUESTION_CODE.test(code)) add(q.id, 'def.bad_question_code', n);
    if (code && seenCodes.has(code.toLowerCase())) add(q.id, 'def.duplicate_question_code', n, code);
    if (code) seenCodes.add(code.toLowerCase());
    if ([...(q.tooltip ?? '')].length > MAX_TOOLTIP_LENGTH) add(q.id, 'def.tooltip_too_long', n, MAX_TOOLTIP_LENGTH);
    if ([...(q.titleRich ?? '')].length > MAX_RICH_LENGTH || [...(q.descriptionRich ?? '')].length > MAX_RICH_LENGTH) add(q.id, 'def.rich_too_long', n, MAX_RICH_LENGTH);
    if (q.textAlign !== undefined && !TEXT_ALIGNS.includes(q.textAlign)) add(q.id, 'def.bad_align', n);
    if (q.media && ((q.media.type !== 'image' && q.media.type !== 'video') || !isValidMediaUrl(q.media.url))) add(q.id, 'def.bad_media', n);
    const myLoop = loopTitle(q.id);
    // A reference from this question to `id` must stay within the same loop (or point outside every loop).
    const reachable = (id: string) => {
      const other = loopTitle(id);
      return !other || other === myLoop;
    };
    if (hasOptions(q.type) && !q.carryForward) {
      const opts = q.options ?? [];
      const min = q.type === 'text_form' || q.type === 'constant_sum' ? 1 : 2;
      if (opts.length < min) add(q.id, q.type === 'text_form' ? 'def.needs_field' : 'def.needs_two_options', n);
      checkOptions(q, opts, n, add);
    }
    if (q.type === 'matrix') {
      const rows = q.rows ?? [];
      if (rows.length < 1) add(q.id, 'def.needs_row', n);
      const ids = new Set<string>();
      for (const r of rows) {
        if (!ID.test(r.id) || ids.has(r.id)) add(q.id, 'def.bad_option_id', n);
        ids.add(r.id);
        if (!r.label.trim()) add(q.id, 'def.option_label', n);
      }
    }
    if (q.options?.some((o) => o.other) && !isChoice(q.type)) add(q.id, 'def.other_not_allowed', n);
    if ((q.options?.filter((o) => o.other).length ?? 0) > 1) add(q.id, 'def.one_other', n);
    if (q.options?.some((o) => o.score !== undefined && !Number.isFinite(o.score))) add(q.id, 'def.bad_score', n);
    if (q.type === 'rating') {
      const s = effectiveScale(q);
      if (s.min >= s.max || s.max - s.min > 10) add(q.id, 'def.rating_scale', n);
    }
    if (q.type === 'slider') {
      const s = effectiveScale(q);
      const step = s.step ?? 1;
      if (!(s.min < s.max) || !(step > 0) || (s.max - s.min) / step > 1000) add(q.id, 'def.slider_scale', n);
    }
    if (q.type === 'constant_sum' && !(Number.isFinite(q.total ?? DEFAULT_TOTAL) && (q.total ?? DEFAULT_TOTAL) > 0)) add(q.id, 'def.bad_total', n);
    if (q.carryForward) {
      const src = seen.get(q.carryForward.questionId);
      const srcQ = src === undefined ? undefined : def.questions[src];
      if (!srcQ || src! >= i || !isChoice(srcQ.type) || !reachable(srcQ.id)) add(q.id, 'def.bad_carry_forward', n);
      if (!(isChoice(q.type) || q.type === 'ranking')) add(q.id, 'def.carry_forward_type', n);
      if (q.carryForward.mode !== 'selected' && q.carryForward.mode !== 'not_selected') add(q.id, 'def.bad_carry_forward', n);
    }
    const v = q.validation;
    if (v) {
      if (v.min !== undefined && v.max !== undefined && v.min > v.max) add(q.id, 'def.min_gt_max', n);
      if (v.minLength !== undefined && v.maxLength !== undefined && v.minLength > v.maxLength) add(q.id, 'def.minlen_gt_maxlen', n);
      if (v.minSelections !== undefined && v.maxSelections !== undefined && v.minSelections > v.maxSelections) add(q.id, 'def.minsel_gt_maxsel', n);
    }
    if ((q.visibleIf?.conditions ?? []).some((c) => reachesIn(q, c.questionId)) || (q.carryForward && reachesIn(q, q.carryForward.questionId))) add(q.id, 'randomizer.condition', n);
    if (q.visibleIf && q.visibleIf.match !== 'all' && q.visibleIf.match !== 'any') add(q.id, 'def.bad_match', n);
    for (const c of q.visibleIf?.conditions ?? []) {
      const src = seen.get(c.questionId);
      if (src === undefined || src >= i || !reachable(c.questionId)) {
        add(q.id, 'def.condition_earlier', n);
        continue;
      }
      checkCondition(c, def.questions[src]!, n, q.id, add);
    }
    for (const j of jumpsOf(q)) {
      if (j.targetEndingId) {
        if (!endingIds.has(j.targetEndingId)) add(q.id, 'def.jump_ending', n);
      } else {
        const targetIndex = def.questions.findIndex((target) => target.id === j.targetQuestionId);
        const target = def.questions[targetIndex];
        // Inside a loop, a jump stays in the same pass or leaves the loop; from outside, it may only land on a loop's first question.
        const targetLoop = target ? loopTitle(target.id) : undefined;
        const ok = targetIndex > i && (!targetLoop || targetLoop === myLoop || isLoopStart(def, targetIndex));
        if (!target || !ok) add(q.id, 'def.jump_target_later', n);
        else if (reachesIn(q, target.id)) add(q.id, 'randomizer.jump', n);
      }
      if (!OPERATORS.some((op) => op.op === j.operator)) add(q.id, 'def.bad_operator', n, j.operator);
      else if (!['answered', 'not_answered'].includes(j.operator) && (j.value === undefined || j.value === '')) add(q.id, 'def.condition_no_value', n);
    }
  });

  const quotaIds = new Set<string>();
  for (const quota of def.quotas ?? []) {
    const name = quota.name.trim() || quota.id;
    if (!ID.test(quota.id) || quotaIds.has(quota.id)) add(undefined, 'def.bad_quota_id');
    quotaIds.add(quota.id);
    if (!quota.name.trim()) add(undefined, 'def.quota_name');
    if (!Number.isInteger(quota.limit) || quota.limit < 1) add(undefined, 'def.quota_limit', name);
    if (quota.match !== 'all' && quota.match !== 'any') add(undefined, 'def.quota_conditions', name);
    if (!quota.conditions.length) add(undefined, 'def.quota_conditions', name);
    for (const c of quota.conditions) {
      const src = seen.get(c.questionId);
      if (src === undefined || loopTitle(c.questionId)) add(undefined, 'def.quota_conditions', name);
      else checkCondition(c, def.questions[src]!, 0, undefined, add, name);
    }
  }
  return issues;
}

/** Structural checks of the randomizers; the engine ignores any that fail (see randomizerGroups). */
function checkRandomizers(def: Definition, add: Add) {
  const list = def.settings.randomizers ?? [];
  if (!list.length) return;
  const runs = sectionRuns(def);
  const ids = new Set<string>();
  const used = new Map<string, number>();
  list.forEach((r, i) => {
    const n = i + 1;
    if (!ID.test(r.id ?? '') || ids.has(r.id)) add(undefined, 'randomizer.bad_id');
    ids.add(r.id);
    const titles = (Array.isArray(r.sections) ? r.sections : []).map((t) => String(t).trim());
    if (titles.length === 0) return; // blank: waiting for its sections, ignored until then
    if (titles.length < 2 || new Set(titles).size !== titles.length || !Number.isInteger(r.present) || r.present < 1 || r.present > titles.length) {
      add(undefined, 'randomizer.count', n, titles.length);
    }
    for (const [t, n] of Object.entries(r.limits ?? {})) {
      if (!Number.isInteger(n) || n < 1 || !titles.includes(t.trim())) add(undefined, 'randomizer.limit', i + 1, t);
    }
    if (r.resetAt && Number.isNaN(Date.parse(r.resetAt))) add(undefined, 'randomizer.reset', i + 1);
    let missing = false;
    for (const t of titles) {
      if (!t || !runs.some((run) => run.title === t)) {
        add(undefined, 'randomizer.section_missing', n, t);
        missing = true;
      }
      const other = used.get(t);
      if (t && other !== undefined && other !== n) add(undefined, 'randomizer.overlap', n, t);
      used.set(t, n);
    }
    if (!missing && titles.length) {
      const idx = runs.flatMap((run, k) => (titles.includes(run.title) ? [k] : []));
      if (idx[idx.length - 1]! - idx[0]! + 1 !== idx.length) add(undefined, 'randomizer.contiguous', n);
    }
  });
}

function isHttpsUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !!u.hostname && url.length <= 2000;
  } catch {
    return false;
  }
}

/** Loop sections by title -> loop question id. */
function loopSections(def: Definition): Map<string, string> {
  const out = new Map<string, string>();
  for (const [title, o] of Object.entries(def.settings.sectionOptions ?? {})) if (o.loopOver) out.set(title, o.loopOver);
  return out;
}

function isLoopStart(def: Definition, index: number): boolean {
  const title = def.questions[index]?.sectionTitle?.trim();
  return !!title && def.questions[index - 1]?.sectionTitle?.trim() !== title;
}

type Add = (questionId: string | undefined, key: CoreMessageKey, ...args: (string | number)[]) => void;

function checkOptions(q: Question, opts: NonNullable<Question['options']>, n: number, add: Add) {
  const ids = new Set<string>();
  for (const o of opts) {
    if (!ID.test(o.id) || ids.has(o.id)) add(q.id, 'def.bad_option_id', n);
    ids.add(o.id);
    if (!o.label.trim()) add(q.id, 'def.option_label', n);
    if (o.imageUrl !== undefined && !isValidMediaUrl(o.imageUrl)) add(q.id, 'def.bad_option_image', n);
  }
}

function checkCondition(c: Condition, src: Question, n: number, qid: string | undefined, add: Add, quota?: string) {
  const bad = (key: CoreMessageKey, ...args: (string | number)[]) => (quota ? add(undefined, 'def.quota_conditions', quota) : add(qid, key, n, ...args));
  const meta = OPERATORS.find((o) => o.op === c.operator);
  if (!meta) bad('def.bad_operator', c.operator);
  else if (meta.needsValue && (c.value === undefined || c.value === '')) bad('def.condition_no_value');
  if (c.row !== undefined && !subItems(src).some((o) => o.id === c.row)) bad('def.bad_condition_row');
}

function checkLanguage(def: Definition, add: Add) {
  if (def.language !== undefined && !isLanguageCode(def.language)) add(undefined, 'def.bad_language', def.language);
  for (const lang of Object.keys(def.translations ?? {})) {
    if (!isLanguageCode(lang) || lang === (def.language || 'en')) add(undefined, 'def.bad_language', lang);
  }
  const th = def.theme;
  if (th) {
    if ((th.primaryColor !== undefined && !isHexColor(th.primaryColor)) || (th.backgroundColor !== undefined && !isHexColor(th.backgroundColor))) add(undefined, 'def.bad_color');
    if (th.logoUrl !== undefined && !isValidMediaUrl(th.logoUrl)) add(undefined, 'def.bad_logo');
    if (th.font !== undefined && !THEME_FONTS.includes(th.font)) add(undefined, 'def.bad_font');
    if (th.zoom !== undefined && !THEME_ZOOMS.includes(th.zoom)) add(undefined, 'def.bad_zoom');
  }
}

/** Ids of resolved questions an answer key may belong to (a loop copy's base, or "Other" text). */
export function answerKeyBase(key: string): string {
  return baseId(key.endsWith(':other') ? key.slice(0, -6) : key);
}
